import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  staffConfigModeQueryKeyPart,
  staffConfigModeQueryParams,
} from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import type { StatAccessQueryScope } from '~/hooks/analyticsStatAccess/statAccessQueryScope';
import { statAccessColumnCatalogPath } from '~/hooks/analyticsStatAccess/statAccessApiPaths';
import { customFetch } from '~/utils/customFetch';

import {
  staffStatColumnCatalogResponseSchema,
  type StaffStatColumnCatalogResponse,
} from './staffStatColumnCatalogSchema';

export type {
  StaffStatCatalogRow,
  StaffStatColumnCatalogResponse,
} from './staffStatColumnCatalogSchema';

export type { StatAccessQueryScope } from '~/hooks/analyticsStatAccess/statAccessQueryScope';

const fetchStaffStatColumnCatalog = async (
  accountType: string,
  accountId: string,
  scope?: StatAccessQueryScope,
): Promise<StaffStatColumnCatalogResponse> => {
  const { data } = await customFetch.get<unknown>(
    statAccessColumnCatalogPath(accountType, accountId, scope?.parentAccountId),
    {
      headers: { 'Content-Type': 'application/json' },
      useNodeEndpoint: true,
      params: staffConfigModeQueryParams(scope?.analyticsReportingMode),
    },
  );
  return staffStatColumnCatalogResponseSchema.parse(data);
};

export const getStaffStatColumnCatalogQueryOptions = (
  accountType: string,
  accountId: string,
  scope?: StatAccessQueryScope,
) =>
  queryOptions({
    queryKey: [
      'staff',
      'stat-column-catalog',
      accountType,
      accountId,
      scope?.parentAccountId ?? null,
      staffConfigModeQueryKeyPart(scope?.analyticsReportingMode),
    ],
    queryFn: () => fetchStaffStatColumnCatalog(accountType, accountId, scope),
    enabled: !!accountType && !!accountId,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });

export const useStaffStatColumnCatalogQuery = (
  accountType: string,
  accountId: string,
  scope?: StatAccessQueryScope,
) =>
  // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
  useQuery(getStaffStatColumnCatalogQueryOptions(accountType, accountId, scope));
